import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import { Panel } from "../components/layout/Panel";
import {
  createAlertForService,
  listHealthChecksForService,
  listChannelsForService,
  type HealthCheck,
} from "../api/client";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { useQuery } from "@tanstack/react-query";
import { useMetadata } from "../hooks/useMetadata";

export function PageAlertNew() {
  const { hierarchy, smtpConfigured } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const navigate = useNavigate();
  const { range } = useTimeRangeFromQuery();
  const { metadata } = useMetadata(serviceSlug, { timeRange: range });

  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState<"health" | "metric">("health");
  const [healthCheckId, setHealthCheckId] = useState<string>("");
  const [metricName, setMetricName] = useState("");
  const [conditionType, setConditionType] = useState<"immediate" | "prolonged">("immediate");
  const [durationMinutes, setDurationMinutes] = useState(5);
  const [healthStatuses, setHealthStatuses] = useState<string[]>(["degraded", "unhealthy"]);
  const [metricOperator, setMetricOperator] = useState("gt");
  const [metricValue, setMetricValue] = useState("");
  const [recurrenceType, setRecurrenceType] = useState<"none" | "every_n_hours" | "daily_at">("none");
  const [recurrenceHours, setRecurrenceHours] = useState(4);
  const [recurrenceTime, setRecurrenceTime] = useState("08:00");
  const [enabled, setEnabled] = useState(true);
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: channels = [] } = useQuery({
    queryKey: ["channels", serviceSlug],
    queryFn: ({ signal }) => listChannelsForService(serviceSlug!, signal),
    enabled: !!serviceSlug,
  });

  const { data: healthChecks = [] } = useQuery({
    queryKey: ["healthChecks", serviceSlug],
    queryFn: ({ signal }) => listHealthChecksForService(serviceSlug!, signal),
    enabled: !!serviceSlug && sourceType === "health",
  });

  const metricNames = metadata?.metrics?.map((m) => m.metric_name) ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceSlug || !name.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      let sourceConfig: Record<string, unknown> = {};
      let conditionConfig: Record<string, unknown> = {};
      let recurrenceConfig: Record<string, unknown> = {};

      if (sourceType === "health") {
        sourceConfig = healthCheckId ? { health_check_id: healthCheckId } : {};
        conditionConfig = {
          statuses: healthStatuses,
          ...(conditionType === "prolonged" && { duration_minutes: durationMinutes }),
        };
      } else {
        sourceConfig = { metric_name: metricName };
        conditionConfig = {
          operator: metricOperator,
          value: parseFloat(metricValue) || 0,
          ...(conditionType === "prolonged" && { duration_minutes: durationMinutes }),
        };
      }

      if (recurrenceType === "every_n_hours") {
        recurrenceConfig = { hours: recurrenceHours };
      } else if (recurrenceType === "daily_at") {
        recurrenceConfig = { time: recurrenceTime };
      }

      await createAlertForService(
        serviceSlug,
        {
          name: name.trim(),
          source_type: sourceType,
          source_config: sourceConfig,
          condition_type: conditionType,
          condition_config: conditionConfig,
          recurrence_type: recurrenceType,
          recurrence_config: recurrenceConfig,
          enabled,
          channel_ids: channelIds,
        },
        undefined,
      );
      navigate(`/services/${encodeURIComponent(serviceSlug)}/alerts`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create alert");
    } finally {
      setSubmitting(false);
    }
  };

  if (!serviceSlug) {
    return (
      <Alert severity="warning">No Service in URL. Please navigate from a product's services list.</Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Panel title="Create Alert">
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceType}
                label="Source"
                onChange={(e) => setSourceType(e.target.value as "health" | "metric")}
              >
                <MenuItem value="health">Health check status</MenuItem>
                <MenuItem value="metric">Metric threshold</MenuItem>
              </Select>
            </FormControl>

            {sourceType === "health" && (
              <FormControl fullWidth>
                <InputLabel>Health check</InputLabel>
                <Select
                  value={healthCheckId}
                  label="Health check"
                  onChange={(e) => setHealthCheckId(e.target.value)}
                >
                  <MenuItem value="">Any health check</MenuItem>
                  {healthChecks.map((hc: HealthCheck) => (
                    <MenuItem key={hc.id} value={hc.id}>
                      {hc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {sourceType === "metric" && (
              <FormControl fullWidth>
                <InputLabel>Metric</InputLabel>
                <Select
                  value={metricName}
                  label="Metric"
                  onChange={(e) => setMetricName(e.target.value)}
                  required
                >
                  {metricNames.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Condition</InputLabel>
              <Select
                value={conditionType}
                label="Condition"
                onChange={(e) => setConditionType(e.target.value as "immediate" | "prolonged")}
              >
                <MenuItem value="immediate">Immediate (value hits threshold)</MenuItem>
                <MenuItem value="prolonged">Prolonged (stays above for duration)</MenuItem>
              </Select>
            </FormControl>

            {conditionType === "prolonged" && (
              <TextField
                label="Duration (minutes)"
                type="number"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10) || 5)}
                inputProps={{ min: 1 }}
              />
            )}

            {sourceType === "health" && (
              <FormControl>
                <InputLabel shrink>Trigger on status</InputLabel>
                <Select
                  multiple
                  value={healthStatuses}
                  label="Trigger on status"
                  onChange={(e) => setHealthStatuses(e.target.value as string[])}
                  renderValue={(v) => v.join(", ")}
                >
                  <MenuItem value="unhealthy">unhealthy</MenuItem>
                  <MenuItem value="degraded">degraded</MenuItem>
                </Select>
              </FormControl>
            )}

            {sourceType === "metric" && (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <FormControl sx={{ minWidth: 100 }}>
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={metricOperator}
                    label="Operator"
                    onChange={(e) => setMetricOperator(e.target.value)}
                  >
                    <MenuItem value="gt">&gt;</MenuItem>
                    <MenuItem value="gte">&gt;=</MenuItem>
                    <MenuItem value="lt">&lt;</MenuItem>
                    <MenuItem value="lte">&lt;=</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Value"
                  type="number"
                  value={metricValue}
                  onChange={(e) => setMetricValue(e.target.value)}
                  required
                  sx={{ flex: 1, minWidth: 120 }}
                />
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Recurrence</InputLabel>
              <Select
                value={recurrenceType}
                label="Recurrence"
                onChange={(e) =>
                  setRecurrenceType(e.target.value as "none" | "every_n_hours" | "daily_at")
                }
              >
                <MenuItem value="none">Once when triggered</MenuItem>
                <MenuItem value="every_n_hours">Every N hours while condition met</MenuItem>
                <MenuItem value="daily_at">Daily at specific time</MenuItem>
              </Select>
            </FormControl>

            {recurrenceType === "every_n_hours" && (
              <TextField
                label="Hours"
                type="number"
                value={recurrenceHours}
                onChange={(e) => setRecurrenceHours(parseInt(e.target.value, 10) || 4)}
                inputProps={{ min: 1 }}
              />
            )}

            {recurrenceType === "daily_at" && (
              <TextField
                label="Time (HH:MM)"
                value={recurrenceTime}
                onChange={(e) => setRecurrenceTime(e.target.value)}
                placeholder="08:00"
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Notification channels</InputLabel>
              <Select
                multiple
                value={channelIds}
                label="Notification channels"
                onChange={(e) => setChannelIds(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((id) => channels.find((c) => c.id === id)?.name || channels.find((c) => c.id === id)?.channel_type || id)
                    .join(", ") || "None"
                }
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                {channels.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <ListItemIcon>
                      <Checkbox checked={channelIds.indexOf(c.id) > -1} />
                    </ListItemIcon>
                    <ListItemText primary={c.name || c.channel_type} />
                  </MenuItem>
                ))}
                {channels.length === 0 && (
                  <MenuItem disabled>
                    No channels — create one on the alerts page first
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
              label="Enabled"
            />

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/services/${encodeURIComponent(serviceSlug)}/alerts`)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? "Creating…" : "Create"}
              </Button>
            </Box>
          </Box>
        </form>
      </Panel>
      <Alert severity="info" sx={{ mt: 2 }}>
        Create notification channels on the alerts page, then assign them to alerts.
        {!smtpConfigured && " Email channel is disabled when SMTP is not configured."}
      </Alert>
    </Box>
  );
}
